import React, { useState, useEffect, useRef } from 'react';
import { AdminUserRecord } from '../types';

interface UsersTableProps {
  users: AdminUserRecord[];
  onViewBooks: (userId: string, userName: string) => void;
}

const ALL_COLUMNS = [
  { id: 'userId',            label: 'User ID',            defaultVisible: true },
  { id: 'name',              label: 'Name',               defaultVisible: true },
  { id: 'email',             label: 'Email',              defaultVisible: true },
  { id: 'phone',             label: 'Phone',              defaultVisible: true },
  { id: 'role',              label: 'Role',               defaultVisible: true },
  { id: 'profileComplete',   label: 'Profile Complete',   defaultVisible: true },
  { id: 'bookCount',         label: 'Books',              defaultVisible: true },
  { id: 'signedUp',          label: 'Signed Up',          defaultVisible: true },
  { id: 'lastActive',        label: 'Last Active',        defaultVisible: false },
];

const STORAGE_KEY = 'admin_users_table_columns';

const getInitialVisibility = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return Object.fromEntries(ALL_COLUMNS.map(c => [c.id, c.defaultVisible]));
};

const roleColors: Record<string, string> = {
  admin:  'bg-purple-100 text-purple-700',
  author: 'bg-blue-100 text-blue-700',
  user:   'bg-gray-100 text-gray-600',
};

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysAgo(iso?: string): string {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff}d ago`;
}

const UsersTable: React.FC<UsersTableProps> = ({ users, onViewBooks }) => {
  const [visible, setVisible] = useState<Record<string, boolean>>(getInitialVisibility);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visible));
  }, [visible]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (users.length === 0) {
    return <div className="px-6 py-12 text-center text-gray-500">No users found</div>;
  }

  return (
    <div>
      <div className="flex justify-end px-4 py-2 border-b border-gray-100">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Columns
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
              {ALL_COLUMNS.map(col => (
                <label key={col.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!visible[col.id]}
                    onChange={() => setVisible(prev => ({ ...prev, [col.id]: !prev[col.id] }))}
                    className="accent-orange-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {visible.userId          && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>}
              {visible.name            && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>}
              {visible.email           && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>}
              {visible.phone           && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>}
              {visible.role            && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>}
              {visible.profileComplete && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>}
              {visible.bookCount       && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Books</th>}
              {visible.signedUp        && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>}
              {visible.lastActive      && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => {
              const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || '—';
              return (
                <tr key={user._id} className="hover:bg-gray-50">
                  {visible.userId          && <td className="px-4 py-4 text-sm text-gray-500 font-mono" title={user._id}>...{user._id.slice(-6)}</td>}
                  {visible.name            && <td className="px-4 py-4 text-sm text-gray-900 font-medium">{fullName}</td>}
                  {visible.email           && <td className="px-4 py-4 text-sm text-gray-700">{user.email || <span className="text-gray-400">—</span>}</td>}
                  {visible.phone           && <td className="px-4 py-4 text-sm text-gray-700">{user.phoneNumber || <span className="text-gray-400">—</span>}</td>}
                  {visible.role            && (
                    <td className="px-4 py-4">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${roleColors[user.role] || 'bg-gray-100 text-gray-600'}`}>
                        {user.role}
                      </span>
                    </td>
                  )}
                  {visible.profileComplete && (
                    <td className="px-4 py-4">
                      {user.profileComplete
                        ? <span className="text-green-600 text-sm font-medium">Complete</span>
                        : <span className="text-amber-600 text-sm font-medium">Incomplete</span>}
                    </td>
                  )}
                  {visible.bookCount && (
                    <td className="px-4 py-4">
                      <span className={`text-sm font-medium ${user.bookCount === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                        {user.bookCount}
                      </span>
                    </td>
                  )}
                  {visible.signedUp   && <td className="px-4 py-4 text-sm text-gray-500">{formatDate(user.createdAt)}</td>}
                  {visible.lastActive && <td className="px-4 py-4 text-sm text-gray-500">{daysAgo(user.lastActiveAt)}</td>}
                  <td className="px-4 py-4">
                    <button
                      onClick={() => onViewBooks(user._id, fullName)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={user.bookCount === 0}
                      title={user.bookCount === 0 ? 'No books yet' : `View ${user.bookCount} book(s)`}
                    >
                      Books ({user.bookCount})
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTable;
